import { DEFAULT_USER_NAME, BN, MarketType } from '@velocity-exchange/sdk';
import { ComputeUnits } from '../@types/types';
import { Commitment } from '@solana/web3.js';
import {
	EXPONENT_POOL_ID,
	JLP_POOL_ID,
	MAIN_POOL_ID,
	SACRED_POOL_ID,
	LST_POOL_ID,
} from '@velocity-exchange/common';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import Env from 'src/environmentVariables/EnvironmentVariables';

export * from './math';

export const brandName = 'Drift';

export const MAX_RECENT_TRADES_TO_CACHE = 100;

/**
 * Explanation for the below parameters:
 *
 * - We're using blockheights to display the tx confirmation countdown in the new market order toasts.
 * - We expect this countdown to be IN SYNC with the time that the txSender in the driftClient throws an error for the expired transaction.
 * - The actual time before a block becomes expired is 151 slots (see https://solana.com/docs/advanced/confirmation)
 * - For some reason, when we fetch lastValidBlockHeight from the RPC, it gives a number +300 instead of +151 as expected. This is a "known" issue (see https://solana.stackexchange.com/questions/6238/why-is-lastvalidblockheight-300-blocks-ahead-than-current-blockheight-if-hashes#comment5177_6240)
 * - To account for the above, we keep the OFFSET as -150 , because adding that to the returned value will bring is back in sync with when the tx sender will throw an error.
 * - ^ note that this basically only applies to the whileValidTxSender ... if we started using a different tx sender we would have to rework this code a bit too, or update the new sending to do a similar confirmation check.
 *
 * ----------------
 *
 * ⭐️ Update 26th August 2024 :: Looks like the lastValidBlockHeight value being returned is correct now, as of this change https://github.com/solana-labs/solana/issues/24526#event-12622363063 .. leaving this comment and constant here for posterity but changing the value to zero
 */
export const TX_CONFIRMATION_EXPIRY_COMMITMENT_LEVEL: Commitment =
	Env.defaultCommitmentLevel;
export const SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY_OFFSET = -150;
export const SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY = 151;

export { MIN_LEFTOVER_SOL } from '@velocity-exchange/common';

/**
 * Banned character matcher for referral links to prevent messed up urls and XSS attempts
 *
 * Matching means a character is banned. Alphanumerical and dashes don't match, everything else matches.
 */
export const referralLinkBannedCharactersRegex = /[\s\n\t?/#<>%&[\]@".]/g;

export const ACCOUNT_HEALTH_PERCENT_WARNING = 15;
export const ACCOUNT_HEALTH_PERCENT_CRITICAL = 5;

export enum PageRoute {
	disclaimer = 'disclaimer',
	privacy = 'privacy',
	terms = 'terms',
	dataPolicy = 'data-policy',
	portfolio = 'portfolio',
	funding = 'funding',
	leaderboards = 'leaderboard',
	redeem = 'redeem',
	borrowLend = 'earn/borrow-lend',
	amplify = 'earn/amplify',
	stake = 'stake',
	bridge = 'bridge',
	swap = 'swap',
	BAL = 'BAL',
	marketMakerRewards = 'mm-rewards',
	stats = 'stats',
	trade = '',
	elections = 'elections',
	bet = 'bet',
	vaults = 'vaults',
	referrals = 'referrals',
}

export const PageRoutes = [
	PageRoute.disclaimer,
	PageRoute.privacy,
	PageRoute.terms,
	PageRoute.dataPolicy,
	PageRoute.portfolio,
	PageRoute.funding,
	PageRoute.leaderboards,
	PageRoute.redeem,
	PageRoute.borrowLend,
	PageRoute.amplify,
	PageRoute.stake,
	PageRoute.bridge,
	PageRoute.swap,
	PageRoute.BAL,
	PageRoute.marketMakerRewards,
	PageRoute.stats,
	PageRoute.elections,
	PageRoute.bet,
	PageRoute.vaults,
	PageRoute.referrals,
	// trade needs to be the last one in the array; since the path is an empty string, it will match any route
	PageRoute.trade,
];

export const NonTradeRoutes = PageRoutes.filter(
	(route) => route !== PageRoute.trade
);

export const NEW_MARKET_CUT_OFF_TIME = Date.now() - 1000 * 60 * 60 * 24 * 7;

export const COINGECKO_API_URL = 'https://pro-api.coingecko.com/api/v3';

export const SUPER_STAKE_ACCOUNT_NAMES = [
	'Super Stake SOL',
	'Super Stake JitoSOL',
	'Super Stake bSOL',
];

export const DEFAULT_ACCOUNT_NAMES_BY_POOL_ID: Record<number, string> = {
	[MAIN_POOL_ID]: DEFAULT_USER_NAME,
	[JLP_POOL_ID]: 'JLP Market - Isolated Pool',
	[EXPONENT_POOL_ID]: 'Exponent Market - Isolated Pool',
	[SACRED_POOL_ID]: 'ACRED Market - Isolated Pool',
	[LST_POOL_ID]: 'LST Market - Isolated Pool',
};

export const POOL_NAMES_BY_POOL_ID: Record<number, string> = {
	[MAIN_POOL_ID]: 'Main Market',
	[JLP_POOL_ID]: 'JLP Market',
	[EXPONENT_POOL_ID]: 'Exponent Market',
	[SACRED_POOL_ID]: 'ACRED Market',
	[LST_POOL_ID]: 'LST Market',
};

export const MAX_NUMBER_OF_PERP_POSITIONS = 8;

export const LP_SUBACCOUNT_NAME = 'Drift Liquidity Provider';
export const NEW_LP_SUBACCOUNT_NAME = 'Backstop AMM Liquidity';

export const WALLET_OPTION_VALUE = 'CONNECTED_WALLET';

export const STABLE_COIN_SYMBOLS = ['usdc', 'usdt', 'USDC', 'USDT'];

export const CUSTOM_RPC_LABEL = 'Custom';

export const S3_BUCKET_DRIFT_PUBLIC_MAINNET =
	'https://drift-public.s3.eu-central-1.amazonaws.com';

export const MARKET_ICON_PATH = `${S3_BUCKET_DRIFT_PUBLIC_MAINNET}/assets/icons/markets`;

export const DRIFT_WALLET_CONNECT_PROJECT_ID =
	'c9af800e9b964d79cff882d8c1ef13f0';

export const PRETTY_ACCOUNT_RENT_MINIMUM = '0.0313';

export const MAX_COMPUTE_UNITS = 1_400_000 as ComputeUnits;

export const DRIFT_DISCORD_URL = 'https://discord.com/invite/95kByNnDy5';

export const DRIFT_TWITTER_URL = 'https://x.com/DriftProtocol';

export const NOTIFICATION_CONFIGURATIONS = [
	{
		value: 'bottomRight',
		label: 'Bottom Right',
	},
	{
		value: 'bottomLeft',
		label: 'Bottom Left',
	},
	{
		value: 'topRight',
		label: 'Top Right',
	},
];

export const DRIFT_GRAFANA_METRICS_URL =
	'https://metrics.drift.trade/d/27H9DCM4z/perp-market-metrics?orgId=1&refresh=1m';

export const REALMS_URL = 'https://app.realms.today/dao/DRIFT';
export const FUTARCHY_URL = 'https://futarchy.metadao.fi/drift';
export const FEE_TIER_DOCS =
	'https://docs.drift.trade/trading/trading-fees#fee-tiers';
export const DRIFT_FOUNDATION_URL = 'https://drift.foundation';
export const DSM_DOCS = 'https://docs.drift.trade/drift-safety-module';

/* hard code an additional number of ticks to apply to the end price of a perp auction */
export const ADDITIONAL_AUCTION_BUFFERS: { marketIndex: number; ticks: BN }[] =
	[{ marketIndex: 62, ticks: new BN(10) }];

// VAULTS UI CONFIG
export const SHOW_VAULTS_ALL_TIME_EARNINGS = false;
export const SHOW_VAULTS_PNL_GRAPH = false;
export const SHOW_VAULTS_APY_GRAPH = false;
export const SHOW_VAULTS_ROI_GRAPH = true;
export const VAULT_VIEW_TAB_OPTIONS = {
	VaultPerformance: 'VaultPerformance',
	UserPerformance: 'UserPerformance',
	Overview: 'Overview',
} as const;

// SLIPPAGE CONFIG
export const SLIPPAGE_PRESETS_MAJORS = [0.1, 0.5, 1];
export const SLIPPAGE_PRESETS_NON_MAJORS = [0.5, 1, 2.5];

export const DEFAULT_MARKET_PROPS = getIsZeroFeesActiveForMarket({
	marketIndex: 1,
	isPerp: true,
})
	? {
			marketIndex: 1,
			marketType: MarketType.PERP,
		}
	: {
			marketIndex: 0,
			marketType: MarketType.PERP,
		};

export const IDLE_BROWSER_CANDLE_REFRESH_GRACE_PERIOD = 10_000; // When the tab goes INACTIVE (that means not focused anymore) then we refresh the candle history upon returning to the tab

export const GEOLOCATION_URL = 'https://geolocation.drift-labs.workers.dev/';
